/** @jsxImportSource @emotion/react */
import React from "react";
import { useForm, Controller } from "react-hook-form";
import { IAuthenticatorProps } from "aws-amplify-react/lib-esm/Auth/Authenticator";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Input, Form, Row, Col, Typography, Button, Alert } from "antd";
import { RegexFormat } from "../../../../common/regexFormat";
import { AuthUsecase } from "../../../../usecases/Auth/usecase";
import * as Styles from "./styles";
import { AuthState } from "../const";
import { useAuthDispatch } from "../../../../usecases/Auth";
import { SEO } from "../../../../components";

interface IFormInputs {
  password: string;
}

const formSchema = yup.object().shape({
  password: yup
    .string()
    .min(8, "Your password must be 8 characters or more.")
    .matches(
      RegexFormat.Password,
      "Password must contain at least one: lower case, upper case, number and symbol character.",
    )
    .required("Please enter password"),
});

type HomeBuilderRequireNewPasswordComponentType = React.FC<IAuthenticatorProps>;

const HomeBuilderRequireNewPasswordComponent: HomeBuilderRequireNewPasswordComponentType =
  ({ onStateChange, authState, authData }: IAuthenticatorProps) => {
    const authDispatch = useAuthDispatch();
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const {
      handleSubmit,
      formState: { errors },
      control,
      reset,
    } = useForm<IFormInputs>({
      mode: "onSubmit",
      resolver: yupResolver(formSchema),
    });

    const handleSubmitHandle = React.useCallback(
      async (data: IFormInputs) => {
        setIsProcessing(true);
        try {
          const authUsecase = new AuthUsecase(authDispatch);
          await authUsecase.completeNewPassword({
            cognitoUser: authData,
            password: data.password,
          });
          reset();
          setErrorMessage("");
        } catch (error) {
          console.error(error);
          if (error.code === "InvalidPasswordException") {
            setErrorMessage(error.message);
          } else {
            let message =
              "There was a problem activating your account. Please ";
            message += `<a href="mailto:feedback@awaydigitalhome.com?subject=Account activation problem">contact us</a> `;
            message += "so we can resolve this for you.";
            setErrorMessage(message);
          }
        } finally {
          setIsProcessing(false);
        }
      },
      [authData, authDispatch, reset],
    );

    const onBackToSignInClickHandle = React.useCallback(() => {
      if (onStateChange) {
        onStateChange("signIn");
        reset();
        setErrorMessage("");
      }
    }, [onStateChange, reset]);

    if (authState !== AuthState.RequireNewPassword && !errorMessage) {
      return <></>;
    }

    return (
      <Form layout="vertical" onFinish={handleSubmit(handleSubmitHandle)}>
        <SEO title="Home Builder Require New Password" />
        <Row justify="center">
          <Col css={Styles.colGutterStyle}>
            <Typography.Title level={2}>New Password</Typography.Title>
          </Col>
        </Row>
        {errorMessage && (
          <Row justify="center">
            <Col css={Styles.colGutterStyle}>
              <Alert
                message={
                  <span dangerouslySetInnerHTML={{ __html: errorMessage }} />
                }
                type="error"
              />
            </Col>
          </Row>
        )}
        <Form.Item
          label="New Password"
          css={Styles.labelStyle}
          validateStatus={errors.password ? "error" : "validating"}
          help={errors.password?.message}
        >
          <Controller
            name="password"
            control={control}
            render={({ field }) => <Input.Password {...field} size="large" />}
          />
        </Form.Item>
        <Row justify="center">
          <Button
            type="link"
            css={Styles.colGutterStyle}
            onClick={onBackToSignInClickHandle}
          >
            Back to Sign In
          </Button>
        </Row>
        <Row>
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            block
            css={Styles.colGutterStyle}
            loading={isProcessing}
          >
            Change
          </Button>
        </Row>
      </Form>
    );
  };

export const HomeBuilderRequireNewPassword = React.memo(
  HomeBuilderRequireNewPasswordComponent,
);
